/**
 * Phase 7.1: UndoRedoService
 *
 * This service enables undo/redo functionality for AI-driven property changes.
 * It maintains a history stack of component snapshots that can be restored
 * when users issue "undo" or "redo" commands or use keyboard shortcuts.
 *
 * Key Features:
 * - Captures component state before AI changes
 * - Supports undo via command or Cmd+Z
 * - Supports redo via command or Cmd+Shift+Z
 * - Generates human-readable action descriptions
 * - Limits stack size to prevent memory issues
 *
 * @see Documents/AI_Template_System/Roadmaps/AI_Chat_Enhancement_Roadmap_Phase2.md
 */

import { Component, ResponsivePosition } from '@/types';
import { CSSProperties } from 'react';

/**
 * Represents a snapshot of a component's state before a change was applied.
 */
export interface ComponentSnapshot {
  id: string;
  style?: Record<string, unknown>;
  properties?: Record<string, unknown>;
  responsivePosition?: ResponsivePosition;
  size?: { width: number | string; height: number | string };
}

/**
 * Represents the property changes that were applied to a component.
 */
export interface PropertyChanges {
  style?: CSSProperties;
  properties?: Record<string, unknown>;
  responsivePosition?: Partial<ResponsivePosition>;
  size?: { width?: number | string; height?: number | string };
}

/**
 * Represents an undoable action in the history stack.
 */
export interface UndoableAction {
  /** Unique identifier for this action */
  id: string;
  /** The command that triggered this action */
  command: string;
  /** Human-readable description of what changed */
  description: string;
  /** ID of the component that was modified */
  componentId: string;
  /** Snapshot of the component BEFORE the change */
  beforeSnapshot: ComponentSnapshot;
  /** Snapshot of the component AFTER the change (captured later) */
  afterSnapshot?: ComponentSnapshot;
  /** The property changes that were applied */
  appliedChanges: PropertyChanges;
  /** Timestamp when the action was recorded */
  timestamp: number;
}

/**
 * Current state of the undo/redo system.
 */
export interface UndoRedoState {
  canUndo: boolean;
  canRedo: boolean;
  undoDescription: string | null;
  redoDescription: string | null;
  undoStackSize: number;
  redoStackSize: number;
}

/**
 * Subscriber callback type for state changes.
 */
type StateChangeListener = (state: UndoRedoState) => void;

/**
 * Maximum number of actions to keep in the undo stack.
 */
const MAX_STACK_SIZE = 50;

/**
 * UndoRedoService - Singleton service for managing AI command undo/redo
 */
class UndoRedoService {
  private static instance: UndoRedoService | null = null;

  /** Stack of actions that can be undone */
  private undoStack: UndoableAction[] = [];

  /** Stack of actions that can be redone */
  private redoStack: UndoableAction[] = [];

  /** Subscribers listening for state changes */
  private listeners: Set<StateChangeListener> = new Set();

  private constructor() {
    // Singleton initialized
  }

  /**
   * Get the singleton instance of UndoRedoService
   */
  static getInstance(): UndoRedoService {
    if (!this.instance) {
      this.instance = new UndoRedoService();
    }
    return this.instance;
  }

  /**
   * Record an action that can be undone.
   * Call this BEFORE applying changes to a component.
   *
   * @param command The user's command string
   * @param component The component being modified (current state)
   * @param changes The changes that will be applied
   * @param description Optional human-readable description
   * @returns The created UndoableAction
   */
  recordAction(
    command: string,
    component: Component,
    changes: PropertyChanges,
    description?: string
  ): UndoableAction {
    // Capture the component's current state as the "before" snapshot
    const beforeSnapshot: ComponentSnapshot = {
      id: component.id,
      style: component.style ? { ...component.style } : undefined,
      properties: component.properties ? { ...component.properties } : undefined,
      responsivePosition: component.responsivePosition
        ? structuredClone(component.responsivePosition)
        : undefined,
      size: component.size
        ? { width: component.size.width, height: component.size.height }
        : undefined,
    };

    // Generate a description if not provided
    const actionDescription = description || this.generateDescription(command, changes);

    const action: UndoableAction = {
      id: `undo_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      command,
      description: actionDescription,
      componentId: component.id,
      beforeSnapshot,
      appliedChanges: structuredClone(changes),
      timestamp: Date.now(),
    };

    // Add to undo stack
    this.undoStack.push(action);

    // Trim stack if it exceeds max size
    if (this.undoStack.length > MAX_STACK_SIZE) {
      this.undoStack.shift();
    }

    // Clear redo stack when a new action is recorded
    this.redoStack = [];

    // Notify subscribers
    this.notifyListeners();

    return action;
  }

  /**
   * Capture the component state AFTER a change was applied.
   * Call this immediately after applying changes.
   *
   * @param component The component with updated state
   */
  captureAfterState(component: Component): void {
    if (this.undoStack.length === 0) {
      return;
    }

    const lastAction = this.undoStack[this.undoStack.length - 1];

    // Only capture if this is the same component
    if (lastAction.componentId !== component.id) {
      return;
    }

    // Capture the after state
    lastAction.afterSnapshot = {
      id: component.id,
      style: component.style ? { ...component.style } : undefined,
      properties: component.properties ? { ...component.properties } : undefined,
      responsivePosition: component.responsivePosition
        ? structuredClone(component.responsivePosition)
        : undefined,
      size: component.size
        ? { width: component.size.width, height: component.size.height }
        : undefined,
    };

  }

  /**
   * Undo the last action.
   *
   * @returns The ComponentSnapshot to restore, or null if nothing to undo
   */
  undo(): { snapshot: ComponentSnapshot; action: UndoableAction } | null {
    if (this.undoStack.length === 0) {
      return null;
    }

    const action = this.undoStack.pop();
    if (!action) {
      return null;
    }

    // Move to redo stack
    this.redoStack.push(action);

    // Notify subscribers
    this.notifyListeners();

    return {
      snapshot: action.beforeSnapshot,
      action,
    };
  }

  /**
   * Inspect the next undo action without moving history stacks.
   */
  peekUndo(): { snapshot: ComponentSnapshot; action: UndoableAction } | null {
    if (this.undoStack.length === 0) {
      return null;
    }

    const action = this.undoStack[this.undoStack.length - 1];
    return {
      snapshot: action.beforeSnapshot,
      action,
    };
  }

  /**
   * Redo the last undone action.
   *
   * @returns The ComponentSnapshot to restore (afterSnapshot), or null if nothing to redo
   */
  redo(): { snapshot: ComponentSnapshot; action: UndoableAction } | null {
    if (this.redoStack.length === 0) {
      return null;
    }

    const action = this.redoStack.pop();
    if (!action) {
      return null;
    }

    // Check if afterSnapshot was captured
    if (!action.afterSnapshot) {
      // Push back to redo stack since we can't properly redo
      this.redoStack.push(action);
      return null;
    }

    // Move back to undo stack
    this.undoStack.push(action);

    // Notify subscribers
    this.notifyListeners();

    return {
      snapshot: action.afterSnapshot,
      action,
    };
  }

  /**
   * Inspect the next redo action without moving history stacks.
   */
  peekRedo(): { snapshot: ComponentSnapshot; action: UndoableAction } | null {
    if (this.redoStack.length === 0) {
      return null;
    }

    const action = this.redoStack[this.redoStack.length - 1];
    if (!action.afterSnapshot) {
      return null;
    }

    return {
      snapshot: action.afterSnapshot,
      action,
    };
  }

  /**
   * Check if undo is available.
   */
  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /**
   * Check if redo is available.
   */
  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /**
   * Get the description of what would be undone.
   */
  getUndoDescription(): string | null {
    if (this.undoStack.length === 0) return null;
    return this.undoStack[this.undoStack.length - 1].description;
  }

  /**
   * Get the description of what would be redone.
   */
  getRedoDescription(): string | null {
    if (this.redoStack.length === 0) return null;
    return this.redoStack[this.redoStack.length - 1].description;
  }

  /**
   * Get the current state of the undo/redo system.
   */
  getState(): UndoRedoState {
    return {
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
      undoDescription: this.getUndoDescription(),
      redoDescription: this.getRedoDescription(),
      undoStackSize: this.undoStack.length,
      redoStackSize: this.redoStack.length,
    };
  }

  /**
   * Clear all undo/redo history.
   */
  clearHistory(): void {
    this.undoStack = [];
    this.redoStack = [];

    this.notifyListeners();
  }

  /**
   * Subscribe to state changes.
   *
   * @param listener Callback to invoke when state changes
   * @returns Unsubscribe function
   */
  subscribe(listener: StateChangeListener): () => void {
    this.listeners.add(listener);

    // Immediately call with current state
    listener(this.getState());

    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Get the last action in the undo stack (for inspection).
   */
  getLastAction(): UndoableAction | null {
    if (this.undoStack.length === 0) return null;
    return this.undoStack[this.undoStack.length - 1];
  }

  /**
   * Generate a human-readable description from a command and changes.
   */
  private generateDescription(command: string, changes: PropertyChanges): string {
    const parts: string[] = [];

    // Analyze style changes
    if (changes.style) {
      const styleKeys = Object.keys(changes.style);
      if (styleKeys.includes('fontSize')) {
        parts.push('font size');
      }
      if (styleKeys.includes('color')) {
        parts.push('text color');
      }
      if (styleKeys.includes('backgroundColor')) {
        parts.push('background color');
      }
      if (styleKeys.includes('fontWeight')) {
        parts.push('font weight');
      }
      if (styleKeys.includes('textAlign')) {
        parts.push('text alignment');
      }
      if (styleKeys.includes('opacity')) {
        parts.push('opacity');
      }
      if (styleKeys.includes('padding') || styleKeys.includes('margin')) {
        parts.push('spacing');
      }
      if (styleKeys.includes('borderRadius')) {
        parts.push('border radius');
      }
      if (styleKeys.includes('justifyContent') || styleKeys.includes('alignItems')) {
        parts.push('layout alignment');
      }
      if (styleKeys.includes('gap')) {
        parts.push('gap');
      }
    }

    // Analyze size changes
    if (changes.size) {
      if (changes.size.width !== undefined && changes.size.height !== undefined) {
        parts.push('size');
      } else if (changes.size.width !== undefined) {
        parts.push('width');
      } else if (changes.size.height !== undefined) {
        parts.push('height');
      }
    }

    // Analyze property changes
    if (changes.properties) {
      const propKeys = Object.keys(changes.properties);
      if (propKeys.includes('content') || propKeys.includes('text')) {
        parts.push('content');
      }
      if (propKeys.includes('visible') || propKeys.includes('display')) {
        parts.push('visibility');
      }
    }

    if (parts.length === 0) {
      // Fall back to using the command
      return `Changed: "${command}"`;
    }

    return `Changed ${parts.join(', ')}`;
  }

  /**
   * Notify all listeners of state changes.
   */
  private notifyListeners(): void {
    const state = this.getState();
    this.listeners.forEach((listener) => {
      try {
        listener(state);
      } catch (error) {
        console.error('[Phase 7.1] Error in state listener:', error);
      }
    });
  }
}

// Export singleton instance
export const undoRedoService = UndoRedoService.getInstance();

// Also export the class for testing
export { UndoRedoService };
